import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, Link2Off, Plus, Printer, Store, Trash2, Zap } from 'lucide-react';
import type { ChannelType, Discount, OrderStatus } from '@bakery/shared';
import { formatMoney } from '@bakery/shared';

import { api, apiBase, setApiBase, type ChannelCapabilityRow } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { UsersTab } from './Users.js';
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  Modal,
  MoneyInput,
  SectionHeading,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';

type SettingsTab = 'shop' | 'statuses' | 'discounts' | 'messages' | 'channels' | 'users' | 'device';

export function SettingsScreen() {
  const { can } = useAuth();

  /*
   * Everyone reaches Settings, because "This device" is where the server
   * address and the receipt printer are set — a rider needs that on their own
   * phone. The shop-wide tabs appear only for those who may change them.
   */
  const tabs: { value: SettingsTab; label: string }[] = [
    ...(can('settings.edit')
      ? ([
          { value: 'shop', label: 'Shop' },
          { value: 'statuses', label: 'Order statuses' },
          { value: 'discounts', label: 'Discounts' },
          { value: 'messages', label: 'Messages' },
          { value: 'channels', label: 'Channels' },
        ] as const)
      : []),
    ...(can('users.manage') ? ([{ value: 'users', label: 'Users' }] as const) : []),
    { value: 'device', label: 'This device' },
  ];

  const [tab, setTab] = useState<SettingsTab>(tabs[0]?.value ?? 'device');

  return (
    <div className="mx-auto max-w-3xl px-4 py-5">
      <header className="mb-4">
        <h1 className="font-display text-2xl font-semibold">Settings</h1>
      </header>

      <div className="mb-4">
        <Tabs value={tab} onChange={setTab} tabs={tabs} />
      </div>

      {tab === 'shop' && <ShopTab />}
      {tab === 'statuses' && <StatusesTab />}
      {tab === 'discounts' && <DiscountsTab />}
      {tab === 'messages' && <MessagesTab />}
      {tab === 'channels' && <ChannelsTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'device' && <DeviceTab />}
    </div>
  );
}

function ShopTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings.get });
  const [draft, setDraft] = useState<Record<string, string | boolean | number> | null>(null);

  const business = settings.data?.business;
  const value = (key: string) => (draft?.[key] as string) ?? (business?.[key as keyof typeof business] as string) ?? '';

  const save = useMutation({
    mutationFn: () => api.settings.update({ business: { ...business, ...draft } as never }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setDraft(null);
      toast.push({ tone: 'ok', title: 'Shop details saved' });
    },
  });

  if (!business) return <Skeleton className="h-64" />;

  const set = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((current) => ({ ...current, [key]: event.target.value }));

  return (
    <Card className="space-y-4 p-4">
      <SectionHeading title="Shop details" hint="These appear on receipts and payslips." />

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Trading name">
          <Input value={value('tradingName')} onChange={set('tradingName')} />
        </Field>
        <Field label="Registered name">
          <Input value={value('legalName')} onChange={set('legalName')} />
        </Field>
        <Field label="TIN">
          <Input value={value('tin')} onChange={set('tin')} className="tabular" />
        </Field>
        <Field label="Phone">
          <Input value={value('phone')} onChange={set('phone')} />
        </Field>
      </div>

      <Field label="Address line 1">
        <Input value={value('addressLine1')} onChange={set('addressLine1')} />
      </Field>
      <Field label="Address line 2">
        <Input value={value('addressLine2')} onChange={set('addressLine2')} />
      </Field>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="City">
          <Input value={value('city')} onChange={set('city')} />
        </Field>
        <Field label="Province">
          <Input value={value('province')} onChange={set('province')} />
        </Field>
        <Field label="Postal code">
          <Input value={value('postalCode')} onChange={set('postalCode')} className="tabular" />
        </Field>
      </div>

      <div className="flex justify-end">
        <Button variant="primary" loading={save.isPending} disabled={!draft} onClick={() => save.mutate()}>
          Save
        </Button>
      </div>
    </Card>
  );
}

/** Statuses are data, so this screen genuinely adds, renames and removes them. */
function StatusesTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const statuses = useQuery({ queryKey: ['order-statuses', 'all'], queryFn: () => api.orders.statuses(true) });
  const templates = useQuery({ queryKey: ['templates'], queryFn: api.templates.list });
  const [editing, setEditing] = useState<OrderStatus | null>(null);
  const [creating, setCreating] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => api.orders.deleteStatus(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['order-statuses'] });
      toast.push({
        tone: 'ok',
        title: result.action === 'deactivated' ? 'Status hidden' : 'Status deleted',
        body: result.action === 'deactivated' ? 'Built-in statuses are hidden rather than removed, so old orders still resolve.' : undefined,
      });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not remove', body: error.message }),
  });

  return (
    <div className="space-y-3">
      <Card className="p-4">
        <SectionHeading
          title="Order statuses"
          hint="Rename, recolour, reorder, and choose what each one says to the customer."
          action={
            <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Add status
            </Button>
          }
        />

        <ul className="divide-y divide-[var(--color-line)]">
          {statuses.data?.map((status) => (
            <li key={status.id} className="flex items-center gap-3 py-3">
              <span className="size-3 shrink-0 rounded-full" style={{ background: status.color }} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={cx('text-sm font-medium', !status.isActive && 'line-through opacity-50')}>{status.name}</span>
                  {status.autoNotify && <Badge tone="info">Notifies</Badge>}
                  {status.countsAsPaid && <Badge tone="ok">Paid</Badge>}
                  {status.isTerminal && <Badge tone="neutral">Closes</Badge>}
                </div>
                {status.description && <p className="truncate text-xs text-[var(--color-ink-faint)]">{status.description}</p>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEditing(status)}>Edit</Button>
              <button onClick={() => remove.mutate(status.id)} aria-label={`Remove ${status.name}`}>
                <Trash2 className="size-4 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]" />
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {(editing || creating) && (
        <StatusModal
          status={editing}
          templates={templates.data?.templates ?? []}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function StatusModal({
  status,
  templates,
  onClose,
}: {
  status: OrderStatus | null;
  templates: { id: string; name: string }[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    name: status?.name ?? '',
    description: status?.description ?? '',
    color: status?.color ?? '#A8763C',
    autoNotify: status?.autoNotify ?? false,
    countsAsPaid: status?.countsAsPaid ?? false,
    isTerminal: status?.isTerminal ?? false,
    isActive: status?.isActive ?? true,
    messageTemplateId: status?.messageTemplateId ?? '',
  });

  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, messageTemplateId: form.messageTemplateId || null };
      return status ? api.orders.updateStatus(status.id, body) : api.orders.createStatus(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-statuses'] });
      toast.push({ tone: 'ok', title: 'Status saved' });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={status ? `Edit “${status.name}”` : 'New status'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!form.name} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-[1fr_auto] gap-3">
          <Field label="Name" required>
            <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} autoFocus />
          </Field>
          <Field label="Colour">
            <input
              type="color"
              value={form.color}
              onChange={(event) => setForm({ ...form, color: event.target.value })}
              className="h-11 w-16 cursor-pointer rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-transparent p-1"
            />
          </Field>
        </div>

        <Field label="Description" hint="Shown to staff on the order board.">
          <Input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </Field>

        <Field label="Message to send" hint="Leave empty for a status that does not contact the customer.">
          <Select value={form.messageTemplateId} onChange={(event) => setForm({ ...form, messageTemplateId: event.target.value })}>
            <option value="">No message</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>{template.name}</option>
            ))}
          </Select>
        </Field>

        <div className="space-y-2 rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)] p-3">
          <Toggle label="Send that message automatically" checked={form.autoNotify} onChange={(v) => setForm({ ...form, autoNotify: v })} />
          <Toggle label="Treat orders here as paid" checked={form.countsAsPaid} onChange={(v) => setForm({ ...form, countsAsPaid: v })} />
          <Toggle label="Closes the order (drops off the board)" checked={form.isTerminal} onChange={(v) => setForm({ ...form, isTerminal: v })} />
          <Toggle label="Show on the board" checked={form.isActive} onChange={(v) => setForm({ ...form, isActive: v })} />
        </div>
      </div>
    </Modal>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="flex items-center gap-2.5 text-sm">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="size-4 accent-[var(--color-crust-600)]" />
      {label}
    </label>
  );
}

/*
 * Discounts the till can apply.
 *
 * Two shapes, because a shop needs both: a percentage (the statutory 20% for
 * senior citizens and PWDs) and a fixed amount off (a ₱50 voucher). The maths
 * for both already existed in `computeTotals`; what was missing was any way to
 * add one without editing the database by hand.
 */
function DiscountsTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<Discount | null>(null);
  const [creating, setCreating] = useState(false);

  // Retired ones included: this is the screen where they are managed, and a
  // discount that vanished without trace is indistinguishable from one nobody
  // ever created.
  const discounts = useQuery({
    queryKey: ['discounts', 'all'],
    queryFn: () => api.catalog.discounts(true),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.catalog.deleteDiscount(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['discounts'] });
      toast.push({
        tone: 'ok',
        title: result.action === 'retired' ? 'Discount retired' : 'Discount deleted',
        body:
          result.action === 'retired'
            ? 'It has been used on orders, so it is kept and taken off the till rather than deleted.'
            : undefined,
      });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not remove', body: error.message }),
  });

  return (
    <div className="space-y-3">
      <Card className="p-4">
        <SectionHeading
          title="Discounts"
          hint="A percentage off, or a fixed amount. These are what the till offers at checkout."
          action={
            <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Add discount
            </Button>
          }
        />

        {discounts.isLoading && <Skeleton className="h-24" />}

        {discounts.data?.length === 0 && (
          <p className="py-6 text-center text-sm text-[var(--color-ink-faint)]">
            No discounts yet. The till will offer none until one is added.
          </p>
        )}

        <ul className="divide-y divide-[var(--color-line)]">
          {discounts.data?.map((discount) => (
            <li key={discount.id} className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cx('text-sm font-medium', !discount.isActive && 'line-through opacity-50')}>
                    {discount.name}
                  </span>
                  {discount.isStatutory && <Badge tone="info">Statutory</Badge>}
                  {discount.requiresApproval && <Badge tone="warn">Needs approval</Badge>}
                  {!discount.isActive && <Badge tone="neutral">Retired</Badge>}
                </div>
                <p className="tabular text-xs text-[var(--color-ink-faint)]">
                  {discount.type === 'percent' ? `${discount.value}% off` : `${formatMoney(discount.value)} off`}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEditing(discount)}>
                Edit
              </Button>
              <button onClick={() => remove.mutate(discount.id)} aria-label={`Remove ${discount.name}`}>
                <Trash2 className="size-4 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]" />
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {(editing || creating) && (
        <DiscountModal
          discount={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function DiscountModal({ discount, onClose }: { discount: Discount | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    name: discount?.name ?? '',
    type: discount?.type ?? ('percent' as Discount['type']),
    value: discount?.value ?? 0,
    isStatutory: discount?.isStatutory ?? false,
    requiresApproval: discount?.requiresApproval ?? false,
    isActive: discount?.isActive ?? true,
  });

  /*
   * The two kinds measure different things — percentage points and centavos —
   * so a value carried across a change of kind is meaningless: 500 centavos
   * (₱5.00) would silently become 500% off. The server rejects that; clearing
   * it here means nobody has to meet the rejection.
   */
  function setType(type: Discount['type']) {
    setForm((current) => (current.type === type ? current : { ...current, type, value: 0 }));
  }

  const invalid =
    !form.name.trim() || form.value <= 0 || (form.type === 'percent' && form.value > 100);

  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, name: form.name.trim() };
      return discount ? api.catalog.updateDiscount(discount.id, body) : api.catalog.createDiscount(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discounts'] });
      toast.push({ tone: 'ok', title: 'Discount saved' });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={discount ? `Edit “${discount.name}”` : 'New discount'}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={invalid} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input
            autoFocus
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            placeholder="Senior citizen"
          />
        </Field>

        <Field label="Kind">
          <div className="flex gap-2">
            {(
              [
                { value: 'percent', label: 'Percentage' },
                { value: 'amount', label: 'Fixed amount' },
              ] as const
            ).map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setType(option.value)}
                aria-pressed={form.type === option.value}
                className={cx(
                  'flex-1 rounded-[var(--radius-md)] border px-3 py-2 text-sm font-medium',
                  form.type === option.value
                    ? 'border-crust-500 bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]'
                    : 'border-[var(--color-line-strong)] hover:bg-[var(--color-surface-sunken)]',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Field>

        {form.type === 'percent' ? (
          <Field label="Percentage off" hint="A whole number, up to 100.">
            <div className="relative">
              <Input
                inputMode="numeric"
                value={form.value || ''}
                onChange={(event) =>
                  setForm({ ...form, value: Number(event.target.value.replace(/[^\d]/g, '')) || 0 })
                }
                className="tabular pr-8 text-right"
                placeholder="20"
              />
              <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-[var(--color-ink-faint)]">
                %
              </span>
            </div>
          </Field>
        ) : (
          <Field label="Amount off">
            <MoneyInput value={form.value} onChange={(centavos) => setForm({ ...form, value: centavos })} />
          </Field>
        )}

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isStatutory}
            onChange={(event) => setForm({ ...form, isStatutory: event.target.checked })}
            className="mt-0.5"
          />
          <span>
            Statutory
            <span className="block text-xs text-[var(--color-ink-faint)]">
              Required by law, like the senior citizen and PWD discounts.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.requiresApproval}
            onChange={(event) => setForm({ ...form, requiresApproval: event.target.checked })}
            className="mt-0.5"
          />
          <span>
            Needs approval
            <span className="block text-xs text-[var(--color-ink-faint)]">
              Flagged on the order so a supervisor can check it.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
            className="mt-0.5"
          />
          <span>
            Offered at the till
            <span className="block text-xs text-[var(--color-ink-faint)]">
              Turn this off to retire a discount without losing what it was.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}

function MessagesTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const templates = useQuery({ queryKey: ['templates'], queryFn: api.templates.list });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);

  const selected = templates.data?.templates.find((t) => t.id === selectedId) ?? templates.data?.templates[0];
  const text = body ?? selected?.body ?? '';

  const save = useMutation({
    mutationFn: () => api.templates.update(selected!.id, { body: text }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setBody(null);
      toast.push({ tone: 'ok', title: 'Message saved' });
    },
  });

  const preview = useQuery({
    queryKey: ['template-preview', text],
    queryFn: () => api.templates.preview(text),
    enabled: text.length > 0,
  });

  if (!templates.data) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-3">
      <Card className="p-4">
        <SectionHeading title="Customer messages" hint="What each order status says. Edit freely — these are yours." />

        <Select
          value={selected?.id ?? ''}
          onChange={(event) => {
            setSelectedId(event.target.value);
            setBody(null);
          }}
          className="mb-3"
        >
          {templates.data.templates.map((template) => (
            <option key={template.id} value={template.id}>{template.name}</option>
          ))}
        </Select>

        <Field label="Message">
          <Textarea rows={12} value={text} onChange={(event) => setBody(event.target.value)} className="font-mono text-[13px]" />
        </Field>

        <div className="mt-3">
          <p className="mb-1.5 text-xs font-medium text-[var(--color-ink-soft)]">Tap to insert</p>
          <div className="flex flex-wrap gap-1.5">
            {templates.data.variables.map((variable) => (
              <button
                key={variable.path}
                title={variable.label}
                onClick={() => setBody(`${text}{{${variable.path}}}`)}
                className="rounded-full border border-[var(--color-line-strong)] px-2.5 py-1 font-mono text-[11px] hover:border-crust-400 hover:bg-[var(--color-accent-soft)]"
              >
                {variable.path}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => selected && api.templates.reset(selected.key).then(() => {
              queryClient.invalidateQueries({ queryKey: ['templates'] });
              setBody(null);
              toast.push({ tone: 'ok', title: 'Reset to the shipped wording' });
            })}
          >
            Reset
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={body === null} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      </Card>

      <Card className="p-4">
        <SectionHeading title="Preview" hint="With example values in place of the placeholders." />
        <pre className="selectable rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)] p-3 font-sans text-sm whitespace-pre-wrap">
          {preview.data?.preview ?? '…'}
        </pre>
      </Card>
    </div>
  );
}

/**
 * Channel setup is also where the honest limits of each platform are stated,
 * so nobody discovers mid-service that TikTok cannot be automated.
 */
function ChannelsTab() {
  const channels = useQuery({ queryKey: ['channels'], queryFn: api.inbox.channels });
  const [connecting, setConnecting] = useState<ChannelCapabilityRow | null>(null);

  if (!channels.data) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-3">
      {channels.data.map((capability) => (
        <Card key={capability.type} className="p-4">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-medium">{capability.label}</h3>
                {capability.channel?.status === 'connected' ? (
                  <Badge tone="ok"><Check className="size-3" /> Connected</Badge>
                ) : capability.canSendDirectly ? (
                  <Badge tone="neutral">Not connected</Badge>
                ) : (
                  <Badge tone="warn">Manual only</Badge>
                )}
              </div>

              {capability.limitation && (
                <p className="mt-1.5 flex gap-1.5 text-xs text-[var(--color-ink-soft)]">
                  <AlertCircle className="mt-px size-3.5 shrink-0" />
                  {capability.limitation}
                </p>
              )}
            </div>

            {capability.requiredCredentials.length > 0 && (
              <Button size="sm" icon={<Zap className="size-4" />} onClick={() => setConnecting(capability)}>
                {capability.channel?.status === 'connected' ? 'Update' : 'Connect'}
              </Button>
            )}
          </div>
        </Card>
      ))}

      {connecting && <ConnectChannelModal capability={connecting} onClose={() => setConnecting(null)} />}
    </div>
  );
}

function ConnectChannelModal({ capability, onClose }: { capability: ChannelCapabilityRow; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [name, setName] = useState(capability.channel?.name ?? capability.label);

  const connect = useMutation({
    mutationFn: () => api.inbox.connectChannel({ type: capability.type as ChannelType, name, credentials }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      toast.push({ tone: 'ok', title: `${capability.label} connected` });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not connect', body: error.message }),
  });

  const webhook = `${window.location.origin}/api/webhooks/${capability.type}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Connect ${capability.label}`}
      description={capability.limitation ?? undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={connect.isPending} onClick={() => connect.mutate()}>Connect</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Display name">
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        {capability.requiredCredentials.map((credential) => (
          <Field key={credential.key} label={credential.label} hint={credential.hint}>
            <Input
              type="password"
              autoComplete="off"
              value={credentials[credential.key] ?? ''}
              onChange={(event) => setCredentials({ ...credentials, [credential.key]: event.target.value })}
            />
          </Field>
        ))}

        <Field label="Webhook URL" hint="Paste this into the platform's developer console so messages reach you.">
          <Input readOnly value={webhook} className="font-mono text-xs" onFocus={(event) => event.currentTarget.select()} />
        </Field>
      </div>
    </Modal>
  );
}

/** Per-device settings, kept in local storage rather than on the server. */
function DeviceTab() {
  const toast = useToast();
  const [base, setBase] = useState(apiBase());
  const [printerAddress, setPrinterAddress] = useState(localStorage.getItem('bakery.printer.address') ?? '');

  return (
    <div className="space-y-3">
      <Card className="space-y-3 p-4">
        <SectionHeading title="Server" hint="Where this device finds the shop's data." />
        <Field label="API address" hint="On the Android app, point this at the shop PC — for example http://192.168.1.20:4000/api">
          <Input value={base} onChange={(event) => setBase(event.target.value)} className="font-mono text-xs" />
        </Field>
        <div className="flex justify-end">
          <Button
            onClick={() => {
              setApiBase(base);
              toast.push({ tone: 'ok', title: 'Server address saved', body: 'Reload to connect.' });
            }}
          >
            Save
          </Button>
        </div>
      </Card>

      <Card className="space-y-3 p-4">
        <SectionHeading title="Receipt printer" hint="Bluetooth thermal printers on Android; USB or the print dialog on the web." />
        <Field label="Bluetooth device address" hint="Pair the printer in Android settings first, then paste its MAC address here.">
          <Input
            value={printerAddress}
            onChange={(event) => setPrinterAddress(event.target.value)}
            placeholder="00:11:22:33:44:55"
            className="font-mono text-xs"
          />
        </Field>
        <div className="flex justify-end">
          <Button
            icon={<Printer className="size-4" />}
            onClick={() => {
              localStorage.setItem('bakery.printer.address', printerAddress);
              toast.push({ tone: 'ok', title: 'Printer saved' });
            }}
          >
            Save printer
          </Button>
        </div>
      </Card>
    </div>
  );
}
